import type { ReactElement } from 'react';

import { OTHER } from '@/shared/config';

const chipClass =
  'flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 ' +
  'text-[13.5px] has-checked:border-foreground has-checked:font-medium';

const otherInputClass =
  'h-10 w-full rounded-md border border-border bg-card px-3 text-sm ' +
  'focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary';

export const labelClass = 'text-[12.5px] font-medium text-muted-foreground';

function OtherField({
  id,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1.5 pt-1">
      <label htmlFor={id} className={labelClass}>
        직접 입력
      </label>
      <input
        id={id}
        type="text"
        maxLength={40}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={otherInputClass}
      />
    </div>
  );
}
/**
 * 단일 선택. 선택지가 5~10개면 셀렉트보다 칩이 한눈에 들어온다 —
 * 셀렉트는 열기 전까지 무엇이 있는지 안 보여서 고르는 데 한 박자가 더 든다.
 */
export function ChipGroup({
  legend,
  name,
  options,
  selected,
  onSelect,
  otherValue = '',
  onOtherChange,
  otherPlaceholder = '',
}: {
  legend: string;
  name: string;
  options: readonly string[];
  selected: string;
  onSelect: (value: string) => void;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  otherPlaceholder?: string;
}): ReactElement {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className={`${labelClass} mb-1`}>{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <label key={option} className={chipClass}>
            <input
              type="radio"
              name={name}
              value={option}
              checked={selected === option}
              onChange={() => onSelect(option)}
              className="size-4 accent-primary"
            />
            {option}
          </label>
        ))}
      </div>
      {selected === OTHER && onOtherChange ? (
        <OtherField
          id={`${name}-other`}
          placeholder={otherPlaceholder}
          value={otherValue}
          onChange={onOtherChange}
        />
      ) : null}
    </fieldset>
  );
}

/** 복수 선택. 「기타」를 함께 고르면 입력 칸이 열린다. */
export function ChipMultiGroup({
  legend,
  name,
  options,
  selected,
  onToggle,
  otherValue = '',
  onOtherChange,
  otherPlaceholder = '',
}: {
  legend: string;
  name: string;
  options: readonly string[];
  selected: ReadonlySet<string>;
  onToggle: (value: string) => void;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  otherPlaceholder?: string;
}): ReactElement {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className={`${labelClass} mb-1`}>{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <label key={option} className={chipClass}>
            <input
              type="checkbox"
              name={name}
              value={option}
              checked={selected.has(option)}
              onChange={() => onToggle(option)}
              className="size-4 accent-primary"
            />
            {option}
          </label>
        ))}
      </div>
      {selected.has(OTHER) && onOtherChange ? (
        <OtherField
          id={`${name}-other`}
          placeholder={otherPlaceholder}
          value={otherValue}
          onChange={onOtherChange}
        />
      ) : null}
    </fieldset>
  );
}
